/**
 * Scene lighting and grade, in one place.
 *
 * User report (2026-08-24): "still seems very dark in dungeon" — the second
 * time; a previous pass raised ambient and exposure and did not fix it.
 *
 * Measured rather than eyeballed this time. Sampling the central 70% x 65% of
 * captured frames (excluding the HUD) gave a median luminance of 0-14 out of
 * 255, with 79-98% of pixels below 25 and the *brightest 5%* only reaching
 * 32-43. One capture was 98% pure black. That is not a moody dungeon, it is a
 * blank screen, and it matches this project's own standing note that dark
 * screenshots are not the bar — the bar is whether a player can see and act.
 *
 * **Dominant cause: the colour grade was squaring the whole frame.**
 * `ColorCorrectionShader` computes `mulRGB * pow(texel + addRGB, powRGB)`, and
 * its `powRGB` uniform defaults to `(2, 2, 2)`. The post stack set `mulRGB` and
 * `addRGB` and never touched `powRGB`, so every pixel was squared in linear
 * space before tone mapping — a surface at 0.28 arrived at 0.078. Neutralising
 * that one uniform, alone, moved the median from 61 to 162 and the fraction
 * below luma 25 from 34% to zero: a bigger swing than every light change here
 * combined. It is also why the *previous* brightening pass barely helped —
 * ambient and exposure both feed the value that then gets squared.
 *
 * **Second cause, real but smaller: the point lights were calibrated for a
 * lighting model three.js no longer has.** `useLegacyLights` was removed in
 * r165 and this project is on r185, so point-light intensity is candela and
 * illuminance falls off as `intensity / distance^2`. A brazier at
 * `intensity: 1.2` delivered ~0.13 at 3m and ~0.03 at 6m — nothing. Ambient and
 * directional lights have no distance term and were unaffected, which is why
 * the scene had a flat wash and no light *pools*: every local light in the
 * dungeon contributed essentially zero.
 *
 * I diagnosed the second cause first and was confident about it in writing
 * before finding the first. It is left here because it is genuinely a bug and
 * the braziers really did emit nothing — but it was not what made the dungeon
 * unreadable, and the ordering matters for anyone tuning this later: check the
 * grade before reaching for the lights.
 *
 * Every value below was picked by sweeping it live and reading the luminance
 * histogram back, not by taste. The targets, for the same central region:
 *
 * | Metric | Target | Why |
 * |---|---|---|
 * | median luma | >= 55 | the typical pixel is readable on a bright display |
 * | fraction < 25 | <= 25% | a quarter of the frame may be genuinely dark, not most of it |
 * | p95 luma | >= 140 | there are real highlights, so the frame has depth rather than uniform grey |
 *
 * The last row is what keeps this a dungeon. Flattening everything to a
 * uniform mid-grey would pass the first two and look worse; the point is
 * bright pools and dark corners, not an evenly-lit room.
 */

/**
 * Global fill — the legibility floor for surfaces no local light reaches.
 *
 * Landed at 1.7 by coming *down* from 4.2, not up from the old 2.4: 4.2 was
 * what it took to see anything while the grade was still squaring the frame,
 * and once that was fixed the same value washed the dungeon flat. Every extra
 * unit of ambient is contrast lost, so this is the smallest value that keeps
 * unlit wall readable.
 *
 * `lighting.md` Core Rule #4 is worth restating: light has no observer-side
 * gameplay effect, so ambient is purely presentation and owes nothing to the
 * sim's own `isLit` / sight-radius model.
 */
export const AMBIENT_COLOR = 0x8a8aaa;
export const AMBIENT_INTENSITY = 1.7;

/**
 * Sun-ish key. Shadowless, so it reaches every surface regardless of the
 * ceiling — which is what made floors read and walls stay black, since a
 * vertical face barely faces it. Raised modestly and aimed shallower so wall
 * faces catch some of it.
 */
export const DIRECTIONAL_COLOR = 0xfff2d8;
export const DIRECTIONAL_INTENSITY = 1.7;
export const DIRECTIONAL_POSITION: readonly [number, number, number] = [18, 22, 12];

/**
 * A second, opposing fill at low intensity. Without it every wall facing away
 * from the key is lit by ambient alone, which is the single biggest reason
 * corridors read as black boxes — a corridor has two facing walls and the key
 * can only ever favour one of them.
 */
export const FILL_COLOR = 0x9fb0d8;
export const FILL_INTENSITY = 0.8;
export const FILL_POSITION: readonly [number, number, number] = [-16, 9, -14];

/**
 * Braziers, in candela. `BRAZIER_INTENSITY / distance^2` is the illuminance,
 * so this number has to be roughly the square of the radius you want it to
 * matter at: 34 gives ~3.8 at 3m and ~0.9 at 6m, which reads as a pool with a
 * falloff rather than as a decorative sprite that emits nothing.
 *
 * `LIGHTING.BRAZIER_LIGHT_RADIUS_M` on the server is 6m and is a *gameplay*
 * number (it feeds `isLit`, which selects sight radius). This is the render
 * range and is deliberately larger: a light that stops dead exactly where the
 * sim stops caring produces a visible hard edge on the floor.
 */
export const BRAZIER_COLOR = 0xffb055;
export const BRAZIER_INTENSITY = 22;
export const BRAZIER_RANGE_M = 14;

/**
 * How many real brazier lights the scene owns, handed to the torches nearest
 * the camera (2026-10-15, QA #9 — see `render/light-pool.ts`). A floor has ~20
 * sconces and every one of them used to be shaded by every fragment. Eight
 * covers every torch inside `BRAZIER_RANGE_M` of the camera in the densest
 * rooms measured, so nothing visibly lit loses its light.
 */
export const BRAZIER_LIGHT_SLOTS = 8;
/** Seconds for a pooled light to fade in when it takes over a new torch, so a hand-off is never a pop. */
export const LIGHT_SLOT_FADE_S = 0.35;

/**
 * The player lantern. Weaker than a brazier on purpose — "a personal light is
 * modest, not a room-filling one" — but it now has to be weak *in candela*,
 * which is a different number from weak in the old model.
 *
 * And weaker still than the pool size implies, because the light is parented
 * inside the character carrying it: at the ~0.3m from the lamp to the model's
 * own surface, inverse-square delivers over 100x what it delivers at 4m. Tuned
 * against the near field (does the holder blow out) rather than the far field
 * (is the pool big enough), because the near field clips first and clipping is
 * what actually looks broken.
 */
export const LANTERN_COLOR = 0xffbb66;
export const LANTERN_INTENSITY = 3.5;
export const LANTERN_RANGE_M = 8;
export const LANTERN_HEIGHT_M = 1.4;
/** Pooled lantern lights — one per party seat. Constant so a lantern lit for the first time no longer changes the light count (and recompiles every shader). */
export const LANTERN_LIGHT_SLOTS = 4;

/**
 * Upper bound on the render pixel ratio (2026-10-15, QA #9). Every pass of the
 * post stack (SSAO, bloom, grade, vignette) runs per *physical* pixel, so a
 * DPR-2 laptop panel paid four times the fill of DPR 1 and a DPR-3 phone nine.
 * 1.5 keeps edges crisp on a high-density screen at just over half the cost
 * of 2; `adaptiveResolution` steps further down only when frames run slow.
 */
export const MAX_PIXEL_RATIO = 1.5;
export const MIN_PIXEL_RATIO = 0.75;

/*
 * There is deliberately NO always-on personal light on characters.
 *
 * One was added in this same pass, on the reasoning that character models are
 * the thing that must stay readable. Measurement says it was solving a problem
 * that the raised ambient and fill already solve, while creating a worse one:
 * a `PointLight` parented at a model's own origin sits *inside* the mesh, and
 * with inverse-square falloff a light at ~0.3m delivers roughly ten times what
 * it delivers at 1m. The character blew out to flat unshaded colour and took
 * the floor under it with it. The same trap is why `LANTERN_INTENSITY` below
 * is far lower than its range would suggest.
 */

/**
 * What fills the frame above the wall line.
 *
 * The dungeon prefabs are open-topped, so any camera pitched even slightly up
 * sees past the walls into the background. At the previous near-black
 * `0x0b0a12` that read as a hole punched in the level rather than as an unlit
 * ceiling: measured on the lit build, the lower two thirds of the frame is
 * only 8-18% dark while the upper third is 76-95%, and almost all of that is
 * this colour.
 *
 * Raised to a dark warm brown that sits below anything lit but above pure
 * black, so the void reads as murk continuous with the stonework. Deliberately
 * a background change and NOT fog: fog would reduce how far a player can see,
 * which is a gameplay property this project already models explicitly on the
 * server (`NOISE.SIGHT_RADIUS_*`), and having the renderer quietly impose a
 * second, different visibility limit would make the two disagree.
 *
 * The real fix is ceiling geometry in the prefabs, which is a content change,
 * not a lighting one.
 */
export const BACKGROUND_COLOR = 0x241d19;

/**
 * Gamma applied by the colour-correction pass. See `post-processing.ts` — the
 * shader's own default is 2.0, i.e. a silent squaring of the whole frame, and
 * that was the dominant cause of the darkness this file exists to fix.
 */
export const GRADE_POW = 1.15;

/** ACES exposure. Above ~1.5 the highlights around braziers clip to white. */
export const TONE_MAPPING_EXPOSURE = 1.1;

/**
 * Vignette reach. `VignetteShader`'s `offset` is how far the darkening extends
 * toward the centre, not how strong it is; `darkness` must stay near 1.0 or
 * the target colour shifts toward grey instead of the effect softening.
 */
export const VIGNETTE_OFFSET = 0.45;
export const VIGNETTE_DARKNESS = 1.0;

/** Bloom threshold, in tone-mapped luminance — only real light sources, never ordinary lit wall. Raised alongside exposure. */
export const BLOOM_THRESHOLD = 1.0;
export const BLOOM_STRENGTH = 0.35;
export const BLOOM_STRENGTH_LOW_TIER = 0.25;
export const BLOOM_RADIUS = 0.6;
